<template>
  <div class="app-container">
    <Sidebar
      :isSidebarVisible="isSidebarVisible"
      @toggle-sidebar="toggleSidebar"
      v-if="$route.meta.showNavBar"
    />

    <div :class="[
      'layout-content-wrapper',
      {
        'layout-content-with-sidebar': $route.meta.showNavBar && (isSidebarVisible || !isMobile),
        'layout-content-no-sidebar': $route.meta.showNavBar && !isSidebarVisible && isMobile
      }
    ]">
      <div class="layout-content ml-auto mr-auto">
        <router-view :class="[$route.meta.showNavBar ? 'p-6 pt-5' : 'p-0']"></router-view>
        <canvas id="canvas" v-show="$route.meta.showCanvasBG"></canvas>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue';
import Footer from '@/components/Footer.vue';
import {ref, onMounted, computed, watch} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {useStore} from 'vuex';

import api from '@/utils/axios';

function initBG() {
  const canvas = document.getElementById("canvas");
  if (!canvas) return;

  const ctx = canvas.getContext('2d');

  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  var stars = [],
      FPS = 60,
      x = 100,
      mouse = {
        x: 0,
        y: 0
      };

  for (var i = 0; i < x; i++) {
    stars.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      radius: Math.random() * 1 + 1,
      vx: Math.floor(Math.random() * 50) - 25,
      vy: Math.floor(Math.random() * 50) - 25
    });
  }

  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.globalCompositeOperation = "lighter";

    for (var i = 0, x = stars.length; i < x; i++) {
      var s = stars[i];

      ctx.fillStyle = "#4ade80";
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.radius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = '#4ade80';
      ctx.stroke();
    }

    ctx.beginPath();
    for (var i = 0, x = stars.length; i < x; i++) {
      var starI = stars[i];
      ctx.moveTo(starI.x, starI.y);
      if (distance(mouse, starI) < 150) ctx.lineTo(mouse.x, mouse.y);
      for (var j = 0, x = stars.length; j < x; j++) {
        var starII = stars[j];
        if (distance(starI, starII) < 150) {
          ctx.lineTo(starII.x, starII.y);
        }
      }
    }
    ctx.lineWidth = 0.1;
    ctx.strokeStyle = 'gray';
    ctx.stroke();
  }

  function distance(point1, point2) {
    var xs = 0;
    var ys = 0;

    xs = point2.x - point1.x;
    xs = xs * xs;

    ys = point2.y - point1.y;
    ys = ys * ys;

    return Math.sqrt(xs + ys);
  }

  function update() {
    for (var i = 0, x = stars.length; i < x; i++) {
      var s = stars[i];

      s.x += s.vx / FPS;
      s.y += s.vy / FPS;

      if (s.x < 0 || s.x > canvas.width) s.vx = -s.vx;
      if (s.y < 0 || s.y > canvas.height) s.vy = -s.vy;
    }
  }

  canvas.addEventListener('mousemove', function (e) {
    mouse.x = e.clientX;
    mouse.y = e.clientY;
  });

  function tick() {
    draw();
    update();
    requestAnimationFrame(tick);
  }

  tick();
}

export default {
  name: 'App',
  components: {Sidebar, Footer},
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const isSidebarVisible = ref(false);
    const isMobile = ref(window.innerWidth < 992);

    const checkUser = async () => {
      try {
        const token = localStorage.getItem('ahToken');
        const isLogged = localStorage.getItem('ahIsLogged');

        if (route.name === 'login' && (!token || isLogged !== 'true')) {
          return;
        }

        if (!token || isLogged !== 'true') {
          if (route.name !== 'login') {
            router.push({name: 'login'});
          }
          return;
        }

        if (store.state.users.isValidating) {
          return;
        }
      } catch (e) {
      }
    }

    const toggleSidebar = () => {
      isSidebarVisible.value = !isSidebarVisible.value;
      store.commit('modals/toggleSidebar');
    };

    // Handle resize to adjust sidebar state
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 992;

      if (newIsMobile !== isMobile.value) {
        isMobile.value = newIsMobile;
        store.commit('modals/setSidebarMobile', newIsMobile);

        if (newIsMobile) {
          // On mobile - hide sidebar
          isSidebarVisible.value = false;
          store.commit('modals/setSidebarVisible', false);
        } else {
          // On desktop - show sidebar
          isSidebarVisible.value = true;
          store.commit('modals/setSidebarVisible', true);
        }
      }
    };

    onMounted(() => {
      // Initialize theme
      const isTheme = localStorage.getItem('isTheme');
      if (isTheme === 'dark') {
        document.documentElement.classList.add("dark-mode");
      } else {
        document.documentElement.classList.remove("dark-mode");
      }

      // Initialize sidebar state in store
      const initialIsMobile = window.innerWidth < 992;
      isMobile.value = initialIsMobile;
      store.commit('modals/setSidebarMobile', initialIsMobile);

      if (initialIsMobile) {
        // On mobile devices, hidden by default
        isSidebarVisible.value = false;
        store.commit('modals/setSidebarVisible', false);
      } else {
        // On desktop, visible by default
        isSidebarVisible.value = true;
        store.commit('modals/setSidebarVisible', true);
      }

      // Initialize background
      try {
        initBG();
      } catch (e) {
      }

      // Check user
      checkUser();

      // Initialize theme in store
      store.dispatch('initializeTheme');

      // Initialize navigation history
      store.dispatch('navigation/initHistory');

      // Add resize listener
      window.addEventListener('resize', handleResize);
    });

    // Sync with store state
    watch(() => store.state.modals.sidebar.isSidebarVisible, (newVal) => {
      isSidebarVisible.value = newVal;
    });

    return {
      isSidebarVisible,
      isMobile,
      toggleSidebar
    };
  },
  mounted() {
  }
}
</script>

<style>
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;
}

/* Prevent scrolling on login page */
.app-container:has(> .layout-content-wrapper > .layout-content > [data-v-component="LoginView"]) {
  overflow: hidden;
  height: 100vh;
}

.layout-content-wrapper {
  flex: 1;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: margin-left;
}

@media screen and (min-width: 1472px) {
  .layout-content {
    max-width: 1572px;
    margin-right: auto !important;
  }
}

/* Content margins for sidebar */
.layout-content-with-sidebar {
  margin-left: 70px; /* Width of collapsed sidebar */
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: margin-left;
}

.layout-content-no-sidebar {
  margin-left: 0; /* No sidebar on mobile */
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: margin-left;
}

@media screen and (max-width: 991px) {
  .layout-content-with-sidebar {
    margin-left: 0; /* No margin on mobile when sidebar is hidden */
    transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: margin-left;
  }

  .layout-content-no-sidebar {
    margin-left: 0; /* No margin when sidebar is hidden */
    transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: margin-left;
  }
}

.p-4 {
  padding: 1rem;
}
</style>
